import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const SettingsContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convert a stored asset path (either "/api/uploads/..." OR full URL) to absolute URL
export function assetUrl(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) return path;
  if (path.startsWith("/")) return `${BACKEND_URL}${path}`;
  return path;
}

// Apply theme (colors + fonts) to :root via CSS variables + <link> for Google Fonts
function applyTheme(theme) {
  if (!theme || typeof document === "undefined") return;

  const root = document.documentElement;
  const hexToHsl = (hex) => {
    if (!hex || typeof hex !== "string") return null;
    let m = hex.trim().replace(/^#/, "");
    if (m.length === 3) m = m.split("").map((c) => c + c).join("");
    if (m.length !== 6) return null;
    const r = parseInt(m.slice(0, 2), 16) / 255;
    const g = parseInt(m.slice(2, 4), 16) / 255;
    const b = parseInt(m.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
        default: break;
      }
      h /= 6;
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };

  const setHslVar = (name, hex) => {
    const hsl = hexToHsl(hex);
    if (hsl) root.style.setProperty(name, hsl);
  };

  setHslVar("--primary", theme.primary);
  setHslVar("--ring", theme.primary);
  setHslVar("--secondary", theme.secondary);
  setHslVar("--accent", theme.accent);
  // Do NOT set --foreground / --background / --brand-bg / --brand-dark here —
  // those are owned by ThemeContext (light/dark mode) and get overridden per mode.

  root.style.setProperty("--brand-primary", theme.primary || "#1E3A8A");
  root.style.setProperty("--brand-secondary", theme.secondary || "#F43F5E");
  root.style.setProperty("--brand-accent", theme.accent || "#F59E0B");
  root.style.setProperty("--font-heading", `'${theme.headingFont || "Playfair Display"}'`);
  root.style.setProperty("--font-body", `'${theme.bodyFont || "Manrope"}'`);
  root.style.setProperty("--radius", theme.radius || "1rem");

  // Load Google Fonts dynamically
  const fonts = new Set();
  if (theme.headingFont) fonts.add(theme.headingFont);
  if (theme.bodyFont) fonts.add(theme.bodyFont);
  if (fonts.size === 0) return;
  const families = Array.from(fonts)
    .map((f) => `family=${encodeURIComponent(f).replace(/%20/g, "+")}:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400`)
    .join("&");
  const href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
  const linkId = "dynamic-google-fonts";
  let link = document.getElementById(linkId);
  if (!link) {
    link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.getAttribute("href") !== href) link.setAttribute("href", href);
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      applyTheme(data?.theme);
      if (data?.seo?.title) document.title = data.seo.title;
    } catch (e) {
      // fall back to null; pages will still render with default hard-coded copy
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({ settings, loading, refresh, setSettings, applyTheme }), [settings, loading, refresh]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used inside SettingsProvider");
  return ctx;
}
